import React from "react";
import { Trophy, Receipt, IndianRupee, AlertCircle } from "lucide-react";

interface StaffData {
  staffId: string;
  staffName: string;
  totalOrders: number;
  totalSales: number;
  totalReceived: number;
  totalOutstanding: number;
  latestOrderDate: string;
}

interface Props {
  staffData: StaffData[];
}

const StaffPerformance: React.FC<Props> = ({ staffData }) => {
  const sortedStaff = [...staffData].sort(
    (a, b) => b.totalSales - a.totalSales
  );

  const highestSales = sortedStaff[0]?.totalSales || 1;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            Staff Performance
          </h2>
          <p className="text-sm text-gray-500">
            Staff-wise sales and order insights
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left p-3">Rank</th>
              <th className="text-left p-3">Staff</th>
              <th className="text-left p-3">Orders</th>
              <th className="text-left p-3">Sales</th>
              <th className="text-left p-3">Received</th>
              <th className="text-left p-3">Outstanding</th>
              <th className="text-left p-3">Performance</th>
            </tr>
          </thead>

          <tbody>
            {sortedStaff.map((staff, index) => {
              const performance =
                (staff.totalSales / highestSales) * 100;

              return (
                <tr
                  key={staff.staffId}
                  className="border-b hover:bg-gray-50 transition"
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {index === 0 && (
                        <Trophy className="w-4 h-4 text-yellow-500" />
                      )}
                      #{index + 1}
                    </div>
                  </td>

                  <td className="p-3 font-medium text-gray-800">
                    {staff.staffName}
                  </td>

                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <Receipt className="w-4 h-4 text-gray-500" />
                      {staff.totalOrders}
                    </div>
                  </td>

                  <td className="p-3 font-semibold text-green-600">
                    ₹{staff.totalSales}
                  </td>

                  <td className="p-3 text-blue-600">
                    ₹{staff.totalReceived}
                  </td>

                  <td className="p-3 text-red-500">
                    <div className="flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      ₹{staff.totalOutstanding}
                    </div>
                  </td>

                  <td className="p-3 min-w-[180px]">
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-green-500 h-3 rounded-full"
                        style={{ width: `${performance}%` }}
                      />
                    </div>

                    <span className="text-xs text-gray-500 mt-1 block">
                      {performance.toFixed(0)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StaffPerformance;